import AMPLITUDE_EVENT_PROPERTIES from '../../consts/amplitude/amplitudeEventProperties';
import AMPLITUDE_EVENT_TYPES from '../../consts/amplitude/amplitudeEventTypes';
import AMPLITUDE_EVENT_VALUES from '../../consts/amplitude/amplitudeEventValues';
import * as aemFlexBatteryModeTypes from '../../consts/iot/aemFlexBatteryModeTypes';
import * as autoStartModeTypes from '../../consts/iot/autoStartModeTypes';
import * as flexPuffEngineJouleTypes from '../../consts/iot/flexPuffEngineJouleTypes';
import * as ledModeTypes from '../../consts/iot/ledModeTypes';
import * as responsiveDrawModeTypes from '../../consts/iot/responsiveDrawModeTypes';
import * as vapeCloudSizeTypes from '../../consts/iot/vapeCloudSizeTypes';
import * as vapeSessionModeTypes from '../../consts/iot/vapeSessionModeTypes';
import {selectIsMonoDevice} from '../../state/selectors/iotDevice';
import {getState} from '../../state/store';
import objUtils from '../../utils/objUtils';
import connectivityService from '../device/connectivityService';
import amplitude from './amplitude';

const trackRenameDevice = () => {
    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.RENAME_DEVICE);
};

const trackIllumination = ({mode, isHolder}) => {
    const value =
        mode === ledModeTypes.NIGHT_MODE
            ? AMPLITUDE_EVENT_VALUES.ILLUMINATION_50
            : AMPLITUDE_EVENT_VALUES.ILLUMINATION_100;

    amplitude.trackProductEvent({
        isHolder,
        eventType: AMPLITUDE_EVENT_TYPES.ILLUMINATION_MODE_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackVibration = (settings) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const isAnyOn = objUtils.getKeyByValue(settings, true);
    const value = isAnyOn ? AMPLITUDE_EVENT_VALUES.VIBRATION_CUSTOM : AMPLITUDE_EVENT_VALUES.VIBRATION_OFF;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.VIBRATION_SETTINGS_TOGGLED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackAutoCleaningStarted = () => {
    amplitude.trackHolderEvent(AMPLITUDE_EVENT_TYPES.AUTO_CLEANING_STARTED);
};

const trackAutoCleaningCompleted = () => {
    amplitude.trackHolderEvent(AMPLITUDE_EVENT_TYPES.AUTO_CLEANING_COMPLETED);
};

const trackSmartGestures = (settings) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const isAnyOn = objUtils.getKeyByValue(settings, true);
    const value = isAnyOn ? AMPLITUDE_EVENT_VALUES.SMART_GESTURES_ON : AMPLITUDE_EVENT_VALUES.SMART_GESTURES_OFF;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.SMART_GESTURES_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackAutoStart = (mode) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const value =
        mode === autoStartModeTypes.AUTO_START_STATE_DISABLE
            ? AMPLITUDE_EVENT_VALUES.AUTO_START_OFF
            : AMPLITUDE_EVENT_VALUES.AUTO_START_ON;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.AUTO_START_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackFlexPuff = (mode) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const value =
        mode === flexPuffEngineJouleTypes.FLEX_PUFF_ENGINE_JOULE_DISABLE
            ? AMPLITUDE_EVENT_VALUES.FLEX_PUFF_OFF
            : AMPLITUDE_EVENT_VALUES.FLEX_PUFF_ON;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.FLEX_PUFF_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackFlexBattery = (mode) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const value =
        mode === aemFlexBatteryModeTypes.ECO_MODE
            ? AMPLITUDE_EVENT_VALUES.FLEX_BATTERY_ECO
            : AMPLITUDE_EVENT_VALUES.FLEX_BATTERY_PERFORMANCE;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.FLEX_BATTERY_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackFlexBatteryPause = (isPauseOn) => {
    const isMonoDevice = selectIsMonoDevice(getState());
    const value = isPauseOn
        ? AMPLITUDE_EVENT_VALUES.FLEX_BATTERY_PAUSE_ON
        : AMPLITUDE_EVENT_VALUES.FLEX_BATTERY_PAUSE_OFF;

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.PAUSE_MODE_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
        },
    });
};

const trackVapeCloudSize = (mode) => {
    const value =
        mode === vapeCloudSizeTypes.REDUCED
            ? AMPLITUDE_EVENT_VALUES.VAPE_CLOUD_SIZE_REDUCED
            : AMPLITUDE_EVENT_VALUES.VAPE_CLOUD_SIZE_REGULAR;

    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.VAPE_CLOUD_SIZE_SELECTED, {
        [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: value,
    });
};

const trackResponsiveDraw = (mode) => {
    const getValue = () => {
        switch (mode) {
            case responsiveDrawModeTypes.VIVACE:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_VIVACE;
            case responsiveDrawModeTypes.BUBBLY:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_BUBBLY;
            case responsiveDrawModeTypes.SIREN:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_SIREN;
            case responsiveDrawModeTypes.STACCATO:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_STACCATO;
            case responsiveDrawModeTypes.CRESCENDO:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_CRESCENDO;
            case responsiveDrawModeTypes.DISABLED:
                return AMPLITUDE_EVENT_VALUES.RESPONSIVE_DRAW_OFF;
            default:
                return;
        }
    };

    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.RESPONSIVE_DRAW_SELECTED, {
        [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: getValue(),
    });
};

const trackDeviceLocked = () => {
    const isMonoDevice = selectIsMonoDevice(getState());

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.LOCK_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: AMPLITUDE_EVENT_VALUES.DEVICE_LOCKED,
        },
    });
};

const trackDeviceUnlocked = () => {
    const isMonoDevice = selectIsMonoDevice(getState());

    amplitude.trackProductEvent({
        isHolder: !isMonoDevice,
        eventType: AMPLITUDE_EVENT_TYPES.LOCK_SELECTED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SETTING_VALUE]: AMPLITUDE_EVENT_VALUES.DEVICE_UNLOCKED,
        },
    });
};

const trackVapingSession = ({mode, settings}) => {
    const getValue = () => {
        const isAnyOn = Object.keys(settings).find((x) => settings[x]);

        if (!isAnyOn) return AMPLITUDE_EVENT_VALUES.SMART_GESTURES_OFF;

        switch (mode) {
            case vapeSessionModeTypes.TIME_PUFF:
                return AMPLITUDE_EVENT_VALUES.VAPING_SESSION_SECONDS;
            case vapeSessionModeTypes.COUNT_PUFF:
                return AMPLITUDE_EVENT_VALUES.VAPING_SESSION_PUFFS;
            case vapeSessionModeTypes.DISABLE_PUFF:
                return AMPLITUDE_EVENT_VALUES.SMART_GESTURES_OFF;
            default:
                return;
        }
    };

    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.VAPING_SESSION_SELECTED, {
        [AMPLITUDE_EVENT_PROPERTIES.SETTING_TYPE]: getValue(),
    });
};

const trackLocateMyDeviceStarted = () => {
    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.LOCATE_MY_DEVICE_STARTED, {
        [AMPLITUDE_EVENT_PROPERTIES.MODE]: connectivityService.isUsb()
            ? AMPLITUDE_EVENT_VALUES.USB_MODE
            : AMPLITUDE_EVENT_VALUES.BLUETOOTH_MODE,
    });
};

const trackLocateMyDeviceSuccess = () => {
    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.LOCATE_MY_DEVICE_COMPLETED, {
        [AMPLITUDE_EVENT_PROPERTIES.SUCCESS]: true,
        [AMPLITUDE_EVENT_PROPERTIES.MODE]: connectivityService.isUsb()
            ? AMPLITUDE_EVENT_VALUES.USB_MODE
            : AMPLITUDE_EVENT_VALUES.BLUETOOTH_MODE,
    });
};

const trackLocateMyDeviceError = () => {
    amplitude.trackDeviceEvent(AMPLITUDE_EVENT_TYPES.LOCATE_MY_DEVICE_COMPLETED, {
        [AMPLITUDE_EVENT_PROPERTIES.ERROR_MESSAGE]: AMPLITUDE_EVENT_VALUES.LOCATE_MY_DEVICE_ERROR,
        [AMPLITUDE_EVENT_PROPERTIES.SUCCESS]: false,
        [AMPLITUDE_EVENT_PROPERTIES.MODE]: connectivityService.isUsb()
            ? AMPLITUDE_EVENT_VALUES.USB_MODE
            : AMPLITUDE_EVENT_VALUES.BLUETOOTH_MODE,
    });
};

const trackLocateMyDeviceCompleted = (isSuccess) => {
    if (isSuccess) {
        trackLocateMyDeviceSuccess();
    } else {
        trackLocateMyDeviceError();
    }
};

const trackResetStarted = (isHolder) => {
    amplitude.trackProductEvent({
        isHolder,
        eventType: AMPLITUDE_EVENT_TYPES.RESET_DEVICE_STARTED,
    });
};

const trackResetCompleted = ({isHolder, isSuccess}) => {
    amplitude.trackProductEvent({
        isHolder,
        eventType: AMPLITUDE_EVENT_TYPES.RESET_DEVICE_COMPLETED,
        properties: {
            [AMPLITUDE_EVENT_PROPERTIES.SUCCESS]: isSuccess,
        },
    });
};

export default {
    trackRenameDevice,
    trackIllumination,
    trackVibration,
    trackAutoCleaningStarted,
    trackAutoCleaningCompleted,
    trackSmartGestures,
    trackAutoStart,
    trackFlexPuff,
    trackFlexBattery,
    trackFlexBatteryPause,
    trackVapeCloudSize,
    trackResponsiveDraw,
    trackDeviceLocked,
    trackDeviceUnlocked,
    trackVapingSession,
    trackLocateMyDeviceStarted,
    trackLocateMyDeviceCompleted,
    trackResetStarted,
    trackResetCompleted,
};
